<script setup lang="ts">
import { message } from 'ant-design-vue'
import { ref, watch } from 'vue'
import type { Room } from '@/models'
import { createRoom, updateRoom } from '@/services/rooms'

const props = defineProps<{
  visible: boolean
  room: Room | null
  propertyId: string
}>()

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
  (e: 'saved'): void
}>()

const saving = ref(false)
const formRef = ref()
const form = ref({
  name: '',
  type: 'bedroom',
  floor: 1,
  notes: '',
})

const roomTypes = [
  { value: 'bedroom', label: 'Bedroom' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'living_room', label: 'Living Room' },
  { value: 'dining', label: 'Dining' },
  { value: 'hallway', label: 'Hallway' },
  { value: 'balcony', label: 'Balcony' },
  { value: 'other', label: 'Other' },
]

watch(
  () => props.visible,
  (val) => {
    if (val) {
      if (props.room) {
        form.value = {
          name: props.room.name,
          type: props.room.type,
          floor: props.room.floor,
          notes: props.room.notes || '',
        }
      } else {
        form.value = { name: '', type: 'bedroom', floor: 1, notes: '' }
      }
    }
  },
)

const handleSave = async () => {
  try {
    await formRef.value.validate()
  } catch {
    return
  }

  saving.value = true
  try {
    if (props.room) {
      await updateRoom(props.room.id, {
        name: form.value.name.trim(),
        type: form.value.type as Room['type'],
        floor: form.value.floor,
        notes: form.value.notes || undefined,
      })
      message.success('Room updated')
    } else {
      await createRoom({
        property_id: props.propertyId,
        name: form.value.name.trim(),
        type: form.value.type as Room['type'],
        floor: form.value.floor,
        notes: form.value.notes || undefined,
      })
      message.success('Room created')
    }
    emit('saved')
    emit('update:visible', false)
  } catch (e: any) {
    message.error(e?.message || 'Failed to save')
  } finally {
    saving.value = false
  }
}

const handleClose = () => {
  emit('update:visible', false)
}
</script>

<template>
  <a-modal
    :open="visible"
    :title="room ? 'Edit Room' : 'Add Room'"
    @ok="handleSave"
    @cancel="handleClose"
    :confirm-loading="saving"
    ok-text="Save"
    width="500px"
  >
    <a-form ref="formRef" :model="form" layout="vertical">
      <a-form-item
        label="Room Name"
        name="name"
        :rules="[{ required: true, message: 'Room name is required' }]"
      >
        <a-input v-model:value="form.name" placeholder="e.g. Master Bedroom" />
      </a-form-item>
      <a-form-item label="Type" name="type">
        <a-select v-model:value="form.type" :options="roomTypes" />
      </a-form-item>
      <a-form-item label="Floor" name="floor">
        <a-input-number v-model:value="form.floor" :min="0" style="width: 100%" />
      </a-form-item>
      <a-form-item label="Notes" name="notes">
        <a-textarea v-model:value="form.notes" :rows="2" placeholder="Optional notes" />
      </a-form-item>
    </a-form>
  </a-modal>
</template>
